import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { FaInfoCircle } from 'react-icons/fa';

const CompoundInterestSimulator = () => {
  const [initialCapitalStr, setInitialCapitalStr] = useState("10000");
  const [monthlySavingsStr, setMonthlySavingsStr] = useState("100");
  const [yearsStr, setYearsStr] = useState("20");
  const [annualRateStr, setAnnualRateStr] = useState("5");
  const [compoundingMonthsStr, setCompoundingMonthsStr] = useState("12");

  // Convertir les champs en nombres seulement s'ils ne sont pas vides
  const initialCapital = initialCapitalStr.trim() === '' ? NaN : Number(initialCapitalStr);
  const monthlySavings = monthlySavingsStr.trim() === '' ? NaN : Number(monthlySavingsStr);
  const years = yearsStr.trim() === '' ? NaN : Number(yearsStr);
  const annualRate = annualRateStr.trim() === '' ? NaN : Number(annualRateStr);
  const compoundingMonths = compoundingMonthsStr.trim() === '' ? NaN : Number(compoundingMonthsStr);

  function computeData(initial, monthly, yrs, rate, interval) {
    // Si l'une des valeurs essentielles est NaN, pas de calcul
    if (isNaN(initial) || isNaN(monthly) || isNaN(yrs) || isNaN(rate) || isNaN(interval) || yrs <= 0 || interval <= 0) {
      return [];
    }

    const months = yrs * 12;
    const monthlyRate = (rate / 100) / (12 / interval);
    let capital = initial;
    let totalInvested = initial;
    const data = [];

    data.push({
      year: 0,
      total: initial,
      contributions: initial,
      interests: 0
    });

    for (let i = 1; i <= months; i++) {
      capital += monthly;
      totalInvested += monthly;

      if (i % interval === 0) {
        capital *= (1 + monthlyRate);
      }

      if (i % 12 === 0) {
        data.push({
          year: i / 12,
          total: capital,
          contributions: totalInvested,
          interests: capital - totalInvested
        });
      }
    }

    return data;
  }

  const data = computeData(initialCapital, monthlySavings, years, annualRate, compoundingMonths);

  let finalCapital = NaN;
  let totalInvested = NaN;
  let totalInterests = NaN;

  if (data.length > 0) {
    finalCapital = data[data.length - 1].total;
    totalInvested = initialCapital + (monthlySavings * 12 * years);
    totalInterests = finalCapital - totalInvested;
  }

  const displayValue = (val) => isFinite(val) ? val.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' }) : 'N/A';

  const displayFinalCapital = displayValue(finalCapital);
  const displayTotalInvested = displayValue(totalInvested);
  const displayTotalInterests = displayValue(totalInterests);

  const yTickFormatter = (value) => {
    if (value >= 1000) {
      return (value / 1000) + ' k €';
    }
    return value + ' €';
  };

  const xTickFormatter = (value) => {
    return value === 0 ? "Aujourd'hui" : value + ' ans';
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const currentYear = new Date().getFullYear();
      const yearLabel = (label === 0) ? "Aujourd'hui" : (currentYear + label);

      const interestsVal = payload.find(p => p.dataKey === 'interests')?.value || 0;
      const contributionsVal = payload.find(p => p.dataKey === 'contributions')?.value || 0;
      const totalValue = interestsVal + contributionsVal;
      const displayTotalValue = displayValue(totalValue);
      const displayInterests = displayValue(interestsVal);
      const displayContributions = displayValue(contributionsVal);

      return (
        <div className="custom-tooltip">
          <div className="tooltip-row">
            <span className="tooltip-year">{yearLabel}</span>
            <span className="tooltip-total"> {displayTotalValue}</span>
          </div>
          {label !== 0 && (
            <>
              <div className="tooltip-row">
                <span>Intérêts</span>
                <span style={{ color: '#CCC0A6' }}> {displayInterests}</span>
              </div>
              <div className="tooltip-row">
                <span>Versements</span>
                <span style={{ color: '#F7F3EB' }}> {displayContributions}</span>
              </div>
            </>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="simulator-wrapper">
      <div className="simulator-container">
        <section className="inputs-section">
          <div className="input-row">
            <label>
              Capital initial<span className="info-icon"> <FaInfoCircle />
                <span className="tooltip-text">Montant du capital initial de votre placement</span>
              </span>
            </label>
            <div>
              <input
                type="number"
                value={initialCapitalStr}
                onChange={e => setInitialCapitalStr(e.target.value)}
                placeholder="100 000"
              />
              <span>EUR</span>
            </div>
          </div>

          <div className="input-row">
            <label>
              Épargne mensuelle<span className="info-icon"> <FaInfoCircle />
                <span className="tooltip-text">Le montant ajouté chaque mois à votre investissement.</span>
              </span>
            </label>
            <div>
              <input
                type="number"
                value={monthlySavingsStr}
                onChange={e => setMonthlySavingsStr(e.target.value)}
                placeholder="1 000"
              />
              <span>EUR</span>
            </div>
          </div>

          <div className="input-row">
            <label>
              Horizon de placement<span className="info-icon"> <FaInfoCircle />
                <span className="tooltip-text">Durée totale de l'investissement (max 40 ans).</span>
              </span>
            </label>
            <div>
              <input
                type="number"
                value={yearsStr}
                onChange={e => setYearsStr(e.target.value)}
                placeholder="20"
                max="40"
              />
              <span>ANS</span>
            </div>
          </div>

          <div className="input-row">
            <label>
              Taux d'intérêt<span className="info-icon"> <FaInfoCircle />
                <span className="tooltip-text">Taux annuel moyen de croissance de votre investissement.</span>
              </span>
            </label>
            <div>
              <input
                type="number"
                value={annualRateStr}
                onChange={e => setAnnualRateStr(e.target.value)}
                placeholder="5"
              />
              <span>%</span>
            </div>
          </div>

          <div className="input-row">
            <label>
              Intervalle de versement des intérêts<span className="info-icon"> <FaInfoCircle />
                <span className="tooltip-text">Tous les X mois, les intérêts sont réinvestis (max:12).</span>
              </span>
            </label>
            <div>
              <input
                type="number"
                value={compoundingMonthsStr}
                onChange={e => setCompoundingMonthsStr(e.target.value)}
                placeholder="12"
                max="12"
              />
              <span>MOIS</span>
            </div>
          </div>
        </section>

        <section className="results-section">
          <div className="results-overview">
            <div className="capital-final">
              <p>Capital final</p>
              <p> {displayFinalCapital}</p>
            </div>
            <div className="investments-interests">
              <div>
                <p>Versements</p>
                <p> {displayTotalInvested}</p>
              </div>
              <div>
                <p>Intérêts</p>
                <p> {displayTotalInterests}</p>
              </div>
            </div>
          </div>

          <div className="chart-container" style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <AreaChart data={data}>
                <CartesianGrid strokeDasharray="2 2" stroke="#888" />
                <XAxis
                  dataKey="year"
                  tickFormatter={xTickFormatter}
                  ticks={[0, isNaN(years) ? 0 : years]}
                />
                <YAxis tickFormatter={yTickFormatter}/>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  formatter={(value) => value === 'contributions' ? 'Versements' : (value === 'interests' ? 'Intérêts' : value)}
                />
                <Area
                  type="monotone"
                  dataKey="contributions"
                  name="Versements"
                  stackId="1"
                  stroke="#F7F3EB"
                  fill="#F7F3EB"
                  fillOpacity={0.6}
                  legendType="circle"
                />
                <Area
                  type="monotone"
                  dataKey="interests"
                  name="Intérêts"
                  stackId="1"
                  stroke="#CCC0A6"
                  fill="#CCC0A6"
                  fillOpacity={0.6}
                  legendType="circle"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="summary-text">
            <p>Avec un capital initial de <span>{isNaN(initialCapital) ? 'N/A' : initialCapital.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span>
            et en investissant mensuellement <span>{isNaN(monthlySavings) ? 'N/A' : monthlySavings.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span>
            pendant <span>{isNaN(years) ? 'N/A' : years}</span> ans à <span>{isNaN(annualRate) ? 'N/A' : annualRate}%</span>,
            vous obtenez un capital de <span>{displayFinalCapital}</span>.</p>
          </div>
        </section>
      </div>
      <div className="disclaimer">
        <p>Cet outil sert uniquement à des fins d'information. Il ne doit pas être considéré comme un conseil financier.</p>
      </div>
    </div>
  );
};

export default CompoundInterestSimulator;
